import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'Notary', path: '/notary' },
    { name: 'Partnerships', path: '/partnerships' },
    { name: 'Service Area', path: '/service-area' },
    { name: 'About', path: '/about' },
    { name: 'Fees', path: '/fees' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-white/95 backdrop-blur-md shadow-md'
            : 'bg-white/80 backdrop-blur-sm'
        }`}
      >
        <nav className="w-full px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Hamburger Menu - Far Left */}
            <div className="flex items-center gap-4">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="flex flex-col items-center gap-1 cursor-pointer group"
                aria-label="Toggle menu"
              >
                <div className="flex flex-col gap-1.5">
                  <span className={`block w-7 h-0.5 bg-navy transition-all duration-300 ${isMenuOpen ? 'rotate-45 translate-y-2' : ''}`}></span>
                  <span className={`block w-7 h-0.5 bg-navy transition-all duration-300 ${isMenuOpen ? 'opacity-0' : ''}`}></span>
                  <span className={`block w-7 h-0.5 bg-navy transition-all duration-300 ${isMenuOpen ? '-rotate-45 -translate-y-2' : ''}`}></span>
                </div>
                <span className="text-xs font-body font-semibold text-navy uppercase tracking-wide mt-1 group-hover:text-gold transition-colors">
                  Menu
                </span>
              </button>

              {/* Company Name - Next to Menu */}
              <Link to="/" className="flex items-center group relative">
                <div className="flex flex-col leading-none">
                  <div className="flex items-center gap-1">
                    <span className="text-3xl lg:text-4xl font-heading font-extrabold tracking-tight text-navy group-hover:text-navy-dark transition-colors duration-300">
                      A Burton
                    </span>
                    <span className="text-3xl lg:text-4xl font-heading font-extrabold tracking-tight text-gold group-hover:text-gold-dark transition-colors duration-300">
                      Notary
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="h-[2px] flex-1 bg-gradient-to-r from-gold via-gold-light to-transparent rounded-full"></span>
                    <span className="text-[9px] font-body font-semibold uppercase tracking-[0.25em] text-gold/80">Mobile Notary & Loan Signing</span>
                    <span className="h-[2px] flex-1 bg-gradient-to-l from-gold via-gold-light to-transparent rounded-full"></span>
                  </div>
                </div>
              </Link>
            </div>

            {/* CTA - Right */}
            <div className="hidden lg:flex items-center">
              <Link to="/contact" className="bg-navy hover:bg-navy-dark text-white font-body font-semibold px-6 py-3 rounded-lg transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 whitespace-nowrap cursor-pointer">
                Schedule a Signing
              </Link>
            </div>
          </div>
        </nav>
      </header>

      {/* Full Screen Menu Overlay */}
      <AnimatePresence>
        {isMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 z-40 bg-black/50"
              onClick={() => setIsMenuOpen(false)}
            />
            
            {/* Slide-in Menu Panel */}
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className="fixed top-0 left-0 bottom-0 z-50 w-80 max-w-[85vw] bg-navy shadow-2xl"
            >
              <div className="flex flex-col h-full px-8 py-24 overflow-y-auto">
                {/* Close Button */}
                <button
                  onClick={() => setIsMenuOpen(false)}
                  className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center text-white hover:text-gold transition-colors cursor-pointer"
                  aria-label="Close menu"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>

                {/* Navigation Links */}
                <nav className="flex flex-col gap-5">
                  {navLinks.map((link, index) => (
                    <motion.div
                      key={link.path}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: 0.1 + index * 0.05 }}
                    >
                      <Link
                        to={link.path}
                        onClick={() => setIsMenuOpen(false)}
                        className={`text-xl font-heading font-bold transition-colors hover:text-gold ${
                          location.pathname === link.path ? 'text-gold' : 'text-white'
                        }`}
                      >
                        {link.name}
                      </Link>
                    </motion.div>
                  ))}
                </nav>

                {/* CTA Button */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: 0.5 }}
                  className="mt-10"
                >
                  <Link
                    to="/contact"
                    onClick={() => setIsMenuOpen(false)}
                    className="inline-block bg-gold hover:bg-gold-dark text-white font-body font-semibold px-6 py-3 rounded-lg transition-all duration-300 hover:shadow-xl whitespace-nowrap cursor-pointer"
                  >
                    Schedule a Signing
                  </Link>
                </motion.div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
